import React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { format } from "date-fns";
import { Check, CreditCard, Plus, Trash2 } from "lucide-react";
import { findClient } from "../../lib/clientList";

// Subscriptions, manual payments and the finance KPI board.
const PaymentsTab = ({
  clientSubscriptions,
  clients,
  exportFinancialCSV,
  kpiData,
  paymentMethodLabels,
  paymentSummary,
  payments,
  setAssignSubData,
  setAssignSubOpen,
  setNewPlanData,
  setPaymentData,
  setPaymentDialogOpen,
  setPaymentTarget,
  setSubPlanOpen,
  subscriptionPlans,
  deleteSubscriptionPlan,
  t,
}) => {
  return (
    <div className="payments-view">
      <div className="view-header">
        <h1>{t("payments.title")}</h1>
        <div style={{display: 'flex', gap: '8px'}}>
          <Button variant="outline" onClick={() => { setAssignSubData({ client_id: "", plan_id: "" }); setAssignSubOpen(true); }}>
            <Plus size={16} /> Przypisz pakiet
          </Button>
          <Button onClick={() => { setNewPlanData({ name: "", price: "", duration_days: "30", description: "", features: "" }); setSubPlanOpen(true); }}>
            <Plus size={16} /> Nowy pakiet
          </Button>
        </div>
      </div>

      {/* KPI Row */}
      {kpiData && (
        <div className="payment-summary-grid" style={{marginBottom:16}}>
          <Card className="payment-stat-card">
            <CardContent>
              <span className="payment-stat-value">{kpiData.revenue_this_month} PLN</span>
              <span className="payment-stat-label">
                Przychód (miesiąc)
                {kpiData.revenue_change_pct !== 0 && (
                  <Badge variant={kpiData.revenue_change_pct > 0 ? 'default' : 'destructive'} style={{marginLeft:6, fontSize:10}}>
                    {kpiData.revenue_change_pct > 0 ? '+' : ''}{kpiData.revenue_change_pct}%
                  </Badge>
                )}
              </span>
            </CardContent>
          </Card>
          <Card className="payment-stat-card">
            <CardContent>
              <span className="payment-stat-value">{kpiData.sessions_this_month}</span>
              <span className="payment-stat-label">Sesje (miesiąc)</span>
            </CardContent>
          </Card>
          <Card className="payment-stat-card">
            <CardContent>
              <span className="payment-stat-value">{kpiData.active_subscriptions}</span>
              <span className="payment-stat-label">Aktywne pakiety</span>
            </CardContent>
          </Card>
          {kpiData.overdue_subscriptions > 0 && (
            <Card className="payment-stat-card" style={{borderColor:'var(--destructive)'}}>
              <CardContent>
                <span className="payment-stat-value" style={{color:'var(--destructive)'}}>{kpiData.overdue_subscriptions}</span>
                <span className="payment-stat-label">Przeterminowane</span>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* CSV Export */}
      <div style={{display:'flex', justifyContent:'flex-end', marginBottom:12}}>
        <Button variant="outline" size="sm" onClick={exportFinancialCSV}>
          📊 Eksport CSV
        </Button>
      </div>

      {/* Summary Cards */}
      {paymentSummary && (
        <div className="payment-summary-grid">
          <Card className="payment-stat-card">
            <CardContent>
              <span className="payment-stat-value">{paymentSummary.month_revenue} PLN</span>
              <span className="payment-stat-label">Przychód (ten miesiąc)</span>
            </CardContent>
          </Card>
          <Card className="payment-stat-card">
            <CardContent>
              <span className="payment-stat-value">{paymentSummary.total_revenue} PLN</span>
              <span className="payment-stat-label">Przychód łączny</span>
            </CardContent>
          </Card>
          <Card className="payment-stat-card">
            <CardContent>
              <span className="payment-stat-value">{paymentSummary.active_subscriptions}</span>
              <span className="payment-stat-label">Aktywne subskrypcje</span>
            </CardContent>
          </Card>
          <Card className="payment-stat-card">
            <CardContent>
              <span className="payment-stat-value">{paymentSummary.unpaid_subscriptions}</span>
              <span className="payment-stat-label">Nieopłacone</span>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Subscription Plans */}
      <h2 style={{marginTop: 24, marginBottom: 12}}>Pakiety</h2>
      <div className="plans-grid">
        {subscriptionPlans.filter(p => p.active).map(plan => (
          <Card key={plan.id} className="plan-card">
            <CardHeader>
              <CardTitle>{plan.name}</CardTitle>
              <CardDescription>{plan.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="plan-price">{plan.price} {plan.currency} <span className="plan-duration">/ {plan.duration_days} dni</span></div>
              {plan.features?.length > 0 && (
                <ul className="plan-features">
                  {plan.features.map((f, i) => (
                    <li key={i}><Check size={14} /> {f}</li>
                  ))}
                </ul>
              )}
              <Button variant="ghost" size="sm" style={{ marginTop: 8 }}
                      data-testid={`plan-retire-${plan.id}`}
                      onClick={() => deleteSubscriptionPlan(plan)}>
                <Trash2 size={14} /> Wycofaj
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Client Subscriptions */}
      <h2 style={{marginTop: 24, marginBottom: 12}}>Subskrypcje klientów</h2>
      <div className="subscriptions-list">
        {clientSubscriptions.map(sub => {
          const client = findClient(clients, sub.client_id);
          return (
            <Card key={sub.id} className="subscription-card">
              <CardContent className="subscription-content">
                <div className="subscription-info">
                  <span className="subscription-client">{client?.name || "Klient"}</span>
                  <span className="subscription-plan">{sub.plan_name}</span>
                  <span className="subscription-dates">{sub.start_date} - {sub.end_date}</span>
                </div>
                <div className="subscription-status">
                  <Badge variant={sub.status === 'active' ? 'default' : 'secondary'}>{sub.status === 'active' ? 'Aktywna' : 'Wygasła'}</Badge>
                  <Badge variant={sub.paid ? 'default' : 'destructive'}>{sub.paid ? 'Opłacona' : 'Nieopłacona'}</Badge>
                </div>
                <div className="subscription-price">{sub.price} {sub.currency}</div>
                {!sub.paid && (
                  <Button size="sm" onClick={() => {
                    setPaymentTarget(sub);
                    setPaymentData({ amount: String(sub.price), method: "transfer", notes: "" });
                    setPaymentDialogOpen(true);
                  }}>
                    <CreditCard size={14} /> Zapisz płatność
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Payments */}
      {payments.length > 0 && (
        <>
          <h2 style={{marginTop: 24, marginBottom: 12}}>Ostatnie płatności</h2>
          <div className="payments-list">
            {payments.slice(0, 20).map(p => {
              const client = findClient(clients, p.client_id);
              return (
                <div key={p.id} className="payment-item">
                  <div className="payment-item-info">
                    <span className="payment-item-client">{client?.name || "Klient"}</span>
                    <span className="payment-item-date">{format(new Date(p.created_at), "d MMM yyyy")}</span>
                  </div>
                  <Badge variant="outline">{paymentMethodLabels[p.method] || p.method}</Badge>
                  <span className="payment-item-amount">{p.amount} {p.currency}</span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentsTab;
